import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import API from "../../../services/api";
import { getSafeRedirect, isAuthenticated, setAuthSession } from "../../../services/authService";
import { loginSchema, zodErrorToFirstMessage } from "../../../schemas/formSchemas";
import "./Login.css";
import { Moon, Sun, Loader2 } from "lucide-react";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({
    email: "",
    password: "",
    society_id: "",
  });
  const [loginMethod, setLoginMethod] = useState("email");
  const [showPassword, setShowPassword] = useState(false);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requiresRegistration, setRequiresRegistration] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    full_name: "",
    email_id: "",
    gender: "Other",
    ownership_type: "Resident",
  });
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("theme") || "light";
    } catch (error) {
      return "light";
    }
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  // Secure auto-redirect if session is already active
  useEffect(() => {
    if (isAuthenticated()) {
      const fromState = location.state?.from
        ? `${location.state.from.pathname || ""}${location.state.from.search || ""}${location.state.from.hash || ""}`
        : null;
      const redirectParam = new URLSearchParams(location.search).get("redirect");
      const targetDestination = fromState || redirectParam || "/dashboard";
      const safeDestination = getSafeRedirect(targetDestination, "/dashboard");
      navigate(safeDestination, { replace: true });
    }
  }, [location, navigate]);

  const normalizeMobile = (value) => String(value || "").replace(/\D/g, "").slice(-10);

  const handleOtpChange = (index, e) => {
    const val = e.target.value;
    if (!/^\d*$/.test(val)) return;

    const currentOtp = otp.padEnd(6, ' ').split('');
    currentOtp[index] = val.slice(-1) || ' ';
    const newOtp = currentOtp.join('').trimEnd();
    setOtp(newOtp);

    if (val && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted) {
      setOtp(pasted);
      const focusIndex = Math.min(pasted.length, 5);
      document.getElementById(`otp-${focusIndex}`)?.focus();
    }
  };

  const handleMobileChange = (index, e) => {
    const val = e.target.value;
    if (!/^\d*$/.test(val)) return;

    const currentMobile = mobile.padEnd(10, ' ').split('');
    currentMobile[index] = val.slice(-1) || ' ';
    const newMobile = currentMobile.join('').trimEnd();
    setMobile(newMobile);

    if (val && index < 9) {
      document.getElementById(`mobile-${index + 1}`)?.focus();
    }
  };

  const handleMobileKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      document.getElementById(`mobile-${index - 1}`)?.focus();
    } else if (e.key === 'Enter' && mobile.trim().length === 10) {
      handleSendOtp();
    }
  };

  const handleMobilePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 10);
    if (pasted) {
      setMobile(pasted);
      const focusIndex = Math.min(pasted.length, 9);
      document.getElementById(`mobile-${focusIndex === 10 ? 9 : focusIndex}`)?.focus();
    }
  };

  const handleSendOtp = async () => {
    const normalizedMobile = normalizeMobile(mobile);
    if (normalizedMobile.length !== 10) {
      toast.error("Enter a valid 10-digit mobile number.");
      return;
    }

    setLoading(true);
    try {
      const res = await API.post("/auth/otp/initiate", { mobile: normalizedMobile });
      if (res.data.success) {
        setOtpSent(true);
        toast.success("OTP has been sent to your mobile number.");
      } else {
        toast.error(res.data.message || "Failed to send OTP.");
      }
    } catch (err) {
      toast.error(err.message || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const normalizedMobile = normalizeMobile(mobile);
    if (normalizedMobile.length !== 10) {
      toast.error("Enter a valid 10-digit mobile number.");
      return;
    }
    if (!otp || otp.length < 3) {
      toast.error("Enter the OTP you received.");
      return;
    }

    setLoading(true);
    try {
      const res = await API.post("/auth/otp/verify", { mobile: normalizedMobile, otp: String(otp).trim() });
      if (res.data.success && res.data.token) {
        const hasAccess = Boolean(res.data.user.system_role || res.data.user.has_portal_access);
        if (!hasAccess) {
          toast.error("Access Denied: This portal is restricted to system users only.");
          return;
        }
        toast.success("Login successful!");
        setAuthSession({ token: res.data.token, user: res.data.user });
        const fromState = location.state?.from
          ? `${location.state.from.pathname || ""}${location.state.from.search || ""}${location.state.from.hash || ""}`
          : null;
        const redirectParam = new URLSearchParams(location.search).get("redirect");
        const targetDestination = fromState || redirectParam || "/dashboard";
        const safeDestination = getSafeRedirect(targetDestination, "/dashboard");
        navigate(safeDestination, { replace: true });
        return;
      }

      if (res.data.status === "registration_required") {
        toast.info("OTP verified, but mobile is not registered. Please complete registration below.");
        setRequiresRegistration(true);
        return;
      }

      if (res.data.status === "pending_approval") {
        toast.warn(res.data.message || "Your account is pending admin approval.");
        return;
      }

      if (res.data.status === "rejected") {
        toast.error(res.data.message || "Your registration was rejected.");
        return;
      }

      toast.error(res.data.message || "OTP verification failed.");
    } catch (err) {
      toast.error(err.response?.data?.message || "OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    const parsed = loginSchema.safeParse(form);
    if (!parsed.success) {
      toast.error(zodErrorToFirstMessage(parsed.error));
      return;
    }

    setLoading(true);
    try {
      const res = await API.post("/auth/login", parsed.data);

      if (res.data.success) {
        const hasAccess = Boolean(res.data.user.system_role || res.data.user.has_portal_access);
        if (!hasAccess) {
          toast.error("Access Denied: This portal is restricted to system users only.");
          return;
        }
        toast.success("Login successful!");
        setAuthSession({
          token: res.data.token,
          user: res.data.user,
        });
        const fromState = location.state?.from
          ? `${location.state.from.pathname || ""}${location.state.from.search || ""}${location.state.from.hash || ""}`
          : null;
        const redirectParam = new URLSearchParams(location.search).get("redirect");
        const targetDestination = fromState || redirectParam || "/dashboard";
        const safeDestination = getSafeRedirect(targetDestination, "/dashboard");
        navigate(safeDestination, { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await API.post("/auth/otp/register", {
        mobile_number: normalizeMobile(mobile),
        ...registerForm
      });
      if (res.data.success) {
        toast.success(res.data.message || "Registration successful. Awaiting admin approval.");
        setRequiresRegistration(false);
        setOtpSent(false);
      } else {
        toast.error(res.data.message || "Registration failed.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  if (isAuthenticated()) {
    return (
      <div className="login-page test" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="login-page test">
      <div className="motion-shape shape-1" aria-hidden="true" />
      <div className="motion-shape shape-2" aria-hidden="true" />
      <div className="motion-shape shape-3" aria-hidden="true" />

      <button
        type="button"
        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
        className="theme-toggle-button"
        aria-label="Toggle theme"
        title="Toggle theme"
      >
        {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
      </button>

      <div className="login-card">
        <aside className="login-brand">
          <div className="brand-mark">
            <img src="/splash_logo.png" alt="My Asmita" />
          </div>
        </aside>

        <div className="login-panel">
          <div className="login-header">
            <h1>Welcome Back</h1>
            <p>Please enter your details to sign in.</p>
          </div>

          <div className="login-tabs">
            <button 
              type="button" 
              className={`login-tab ${loginMethod === 'email' ? 'active' : ''}`}
              onClick={() => setLoginMethod('email')}
            >
              Email
            </button>
            <button 
              type="button" 
              className={`login-tab ${loginMethod === 'mobile' ? 'active' : ''}`}
              onClick={() => setLoginMethod('mobile')}
            >
              Mobile OTP
            </button>
          </div>

          <div className="login-forms-grid">
            {loginMethod === 'email' && (
            <form className="login-subform password-form" onSubmit={handleLogin}>
              <h2 className="subform-title" style={{display: 'none'}}>Email / Password</h2>
              <label className="login-label" htmlFor="login-email">
                Email Address
              </label>
              <div className="login-input">
                <span className="login-icon email" aria-hidden="true" />
                <input
                  id="login-email"
                  type="email"
                  placeholder="Email Address"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>

              <label className="login-label" htmlFor="login-password">
                Password
              </label>
              <div className="login-input with-action">
                <span className="login-icon lock" aria-hidden="true" />
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                />
                <button
                  type="button"
                  className="login-action"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((prev) => !prev)}
                />
              </div>

              {/* <label className="login-label" htmlFor="login-society">
                Society ID (optional)
              </label>
              <div className="login-input">
                <span className="login-icon" aria-hidden="true" />
                <input
                  id="login-society"
                  type="number"
                  placeholder="Society ID"
                  value={form.society_id}
                  onChange={(e) => setForm({ ...form, society_id: e.target.value })}
                  min={1}
                />
              </div> */}

              <div className="subform-actions">
                <button className="login-submit" type="submit" disabled={loading}>
                  {loading ? <Loader2 className="animate-spin" size={20} /> : "SIGN IN"}
                </button>
                <button className="login-forgot" type="button">
                  Forgot Password?
                </button>
              </div>
            </form>
            )}

          {requiresRegistration ? (
            <form className="login-subform otp-form" onSubmit={handleRegister}>
              <h2 className="subform-title">Complete Registration</h2>
              <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", marginBottom: "1rem" }}>Mobile: {mobile}</p>
              
              <label className="login-label">Full Name</label>
              <div className="login-input">
                <span className="login-icon user" aria-hidden="true" />
                <input
                  type="text"
                  placeholder="Full Name"
                  value={registerForm.full_name}
                  onChange={(e) => setRegisterForm({ ...registerForm, full_name: e.target.value })}
                  required
                />
              </div>

              <label className="login-label">Email Address</label>
              <div className="login-input">
                <span className="login-icon email" aria-hidden="true" />
                <input
                  type="email"
                  placeholder="Email Address"
                  value={registerForm.email_id}
                  onChange={(e) => setRegisterForm({ ...registerForm, email_id: e.target.value })}
                  required
                />
              </div>

              <label className="login-label">Society ID (Optional)</label>
              <div className="login-input">
                <span className="login-icon" aria-hidden="true" />
                <input
                  type="number"
                  placeholder="Society ID"
                  value={registerForm.society_id || ""}
                  onChange={(e) => setRegisterForm({ ...registerForm, society_id: e.target.value })}
                  min={1}
                />
              </div>

              <div className="subform-actions" style={{ flexDirection: "column", gap: "10px" }}>
                <button className="login-submit" type="submit" disabled={loading}>
                  {loading ? <Loader2 className="animate-spin" size={20} /> : "REGISTER"}
                </button>
                <button 
                  className="login-submit" 
                  style={{ backgroundColor: "transparent", color: "var(--text-color)", border: "1px solid var(--border-color)" }} 
                  type="button" 
                  onClick={() => { setRequiresRegistration(false); setOtpSent(false); }}
                >
                  CANCEL
                </button>
              </div>
            </form>
          ) : (
          loginMethod === 'mobile' && (
          <form className="login-subform otp-form" onSubmit={(e) => {
            e.preventDefault();
            otpSent ? handleVerifyOtp() : handleSendOtp();
          }}>
              <h2 className="subform-title" style={{display: 'none'}}>Mobile / OTP</h2>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '8px' }}>
                <label className="login-label" style={{ margin: 0 }}>
                  {otpSent ? "OTP" : "Mobile Number"}
                </label>
                {otpSent && (
                  <button 
                    type="button" 
                    className="login-forgot" 
                    style={{ fontSize: '12px' }}
                    onClick={() => { setOtpSent(false); setOtp(""); }}
                  >
                    Edit Number
                  </button>
                )}
              </div>

              <div className="otp-container" onPaste={otpSent ? handleOtpPaste : handleMobilePaste} style={{ gap: '6px' }}>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].flatMap((idx) => {
                  const isMobileHidden = otpSent && idx > 5;
                  const val = otpSent ? (otp[idx] || '') : (mobile[idx] || '');
                  
                  const elements = [
                    <input
                      key={`inp-${idx}`}
                      id={otpSent ? `otp-${idx}` : `mobile-${idx}`}
                      type="text"
                      className={`otp-box ${isMobileHidden ? 'hidden' : ''}`}
                      style={{ height: '52px', fontSize: '20px' }}
                      maxLength={1}
                      value={val !== ' ' ? val : ''}
                      onChange={(e) => otpSent ? handleOtpChange(idx, e) : handleMobileChange(idx, e)}
                      onKeyDown={(e) => otpSent ? handleOtpKeyDown(idx, e) : handleMobileKeyDown(idx, e)}
                      disabled={isMobileHidden}
                    />
                  ];
                  
                  if (idx === 2) {
                    elements.push(<span key={`sep-otp`} className={`otp-separator ${!otpSent ? 'hidden' : ''}`} style={{ fontSize: '20px' }}>-</span>);
                  }
                  if (idx === 4) {
                    elements.push(<span key={`sep-mob`} className={`otp-separator ${otpSent ? 'hidden' : ''}`} style={{ fontSize: '20px' }}>-</span>);
                  }
                  
                  return elements;
                })}
              </div>

              <div className="subform-actions">
                <button
                  className="login-submit"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : otpSent ? (
                    "Verify OTP"
                  ) : (
                    "Send OTP"
                  )}
                </button>
              </div>
            </form>
            )
          )}
          </div>

          <div className="login-footer">Copyright 2026 Asmita</div>
        </div>
      </div>
    </div>
  );
};

export default Login;
